import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';

/* Story Editor Prototype
 - Single + Carousel modes
 - Safe-zone enforcement (135px @1080 width)
 - Two-layer rendering (interaction vs export)
 - Tap-to-edit, drag, pinch (pointer-event based)
 - Color palettes and templates (minimal, poetic, testimonial, quote)
 - Export (single -> one image, carousel -> all slides)
*/

const TARGET_W = 1080;
const TARGET_H = 1350;
const SAFE_MARGIN_1080 = 135;

const palettes = [
  { bg: '#82314A', text: '#FADADD' },
  { bg: '#FADADD', text: '#82314A' },
  { bg: '#6A3D4C', text: '#F5CEDA' },
  { bg: '#5A5A42', text: '#F5F2E7' },
  { bg: '#1C2A3D', text: '#F5E9DD' },
  { bg: '#A65D4E', text: '#F3E5DC' },
  { bg: '#CC5500', text: '#FFF3E0' },
  { bg: '#D8CAB8', text: '#3E2C23' },
  { bg: '#B5886A', text: '#FAF5F0' },
  { bg: '#E5C49D', text: '#3B2F2F' },
];

const templates = {
  minimal: (setPreview) =>
    setPreview((prev) => ({
      ...prev,
      titleStyle: { textTransform: 'none', fontStyle: 'normal' },
      bodyStyle: { fontStyle: 'normal' },
    })),
  poetic: (setPreview) =>
    setPreview((prev) => ({
      ...prev,
      titleStyle: { textTransform: 'lowercase', letterSpacing: '1px' },
      bodyStyle: { fontStyle: 'italic', lineHeight: 1.6 },
    })),
  testimonial: (setPreview) =>
    setPreview((prev) => ({
      ...prev,
      titleStyle: { textTransform: 'capitalize' },
      bodyStyle: {
        fontStyle: 'italic',
        border: '1px solid currentColor',
        padding: 15,
        borderRadius: 12,
      },
    })),
  quote: (setPreview) =>
    setPreview((prev) => ({
      ...prev,
      titleStyle: {
        textTransform: 'uppercase',
        letterSpacing: '2px',
        fontWeight: 600,
      },
      bodyStyle: { fontStyle: 'italic', marginTop: 25, lineHeight: 1.6 },
    })),
};

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

export default function App() {
  const containerRef = useRef();
  const interactionRef = useRef();
  const renderRef = useRef();

  const [mode, setMode] = useState('single'); // single | carousel
  const [currentSlide, setCurrentSlide] = useState(0);

  const [slides, setSlides] = useState([
    {
      title: 'Embrace your perfect self',
      body: "Insān Kāmil, the 'Perfect Human', embodies divine attributes fully manifested in a mortal being.",
    },
  ]);

  const [palette, setPalette] = useState(palettes[0]);
  const [templateKey, setTemplateKey] = useState('minimal');
  const [previewStyles, setPreviewStyles] = useState({
    titleStyle: {},
    bodyStyle: {},
  });

  const [blocks, setBlocks] = useState({
    title: { x: 0.08, y: 0.08, scale: 1 },
    body: { x: 0.08, y: 0.25, scale: 1 },
  });

  // local interaction transforms (visual)
  const local = useRef(JSON.parse(JSON.stringify(blocks)));
  const activeRef = useRef('title');

  useEffect(() => {
    local.current = JSON.parse(JSON.stringify(blocks));
  }, []);

  useEffect(() => {
    // apply template on change
    templates[templateKey] && templates[templateKey](setPreviewStyles);
  }, [templateKey]);

  useEffect(() => {
    // sync visuals when blocks / palette / content change
    local.current = JSON.parse(JSON.stringify(blocks));
    applyVisuals();
  }, [blocks, palette, slides, currentSlide]);

  function computeSafe(frameW) {
    const s = SAFE_MARGIN_1080 * (frameW / TARGET_W);
    return { left: s, top: s, right: frameW - s, bottom: null, margin: s };
  }

  function applyVisuals() {
    if (!interactionRef.current) return;
    const frameRect = interactionRef.current.getBoundingClientRect();
    const fw = frameRect.width,
      fh = frameRect.height;
    Object.keys(local.current).forEach((k) => {
      const el = interactionRef.current.querySelector(`[data-block="${k}"]`);
      if (!el) return;
      const b = local.current[k];
      const px = { x: b.x * fw, y: b.y * fh };
      el.style.transform = `translate(${px.x}px, ${px.y}px) scale(${b.scale})`;
      el.style.transformOrigin = 'top left';
    });
  }

  // Pointer gesture handling (basic pan + pinch)
  useEffect(() => {
    const el = interactionRef.current;
    if (!el) return;
    const pointers = new Map();
    let modeG = null;
    let lastDist = null;
    let active = activeRef.current;

    function getRect() {
      return el.getBoundingClientRect();
    }

    function onDown(e) {
      el.setPointerCapture && el.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, e);
      if (pointers.size === 1) modeG = 'pan';
      else if (pointers.size === 2) modeG = 'pinch';
    }
    function centroid() {
      let cx = 0,
        cy = 0;
      for (const p of pointers.values()) {
        cx += p.clientX;
        cy += p.clientY;
      }
      const n = pointers.size || 1;
      return { x: cx / n, y: cy / n };
    }
    function onMove(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, e);
      if (!active) active = activeRef.current;
      const rect = getRect();
      const fw = rect.width,
        fh = rect.height;
      if (modeG === 'pan' && pointers.size === 1) {
        const p = Array.from(pointers.values())[0];
        if (!local.current[active]._last)
          local.current[active]._last = { x: p.clientX, y: p.clientY };
        const dx = e.clientX - local.current[active]._last.x;
        const dy = e.clientY - local.current[active]._last.y;
        local.current[active]._last = { x: e.clientX, y: e.clientY };
        const curPx = {
          x: local.current[active].x * fw + dx,
          y: local.current[active].y * fh + dy,
        };
        // measure element box
        const node = el.querySelector(`[data-block="${active}"] .edit`);
        const r = node
          ? node.getBoundingClientRect()
          : { width: 120, height: 40 };
        const clamped = clampWithinSafe(
          curPx.x,
          curPx.y,
          r.width,
          r.height,
          fw
        );
        const norm = { x: clamped.x / fw, y: clamped.y / fh };
        local.current[active].x = norm.x;
        local.current[active].y = norm.y;
        applyVisuals();
      } else if (modeG === 'pinch' && pointers.size === 2) {
        const pts = Array.from(pointers.values());
        const dx = pts[0].clientX - pts[1].clientX;
        const dy = pts[0].clientY - pts[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (lastDist == null) lastDist = dist;
        const delta = dist / lastDist;
        lastDist = dist;
        const newScale = (local.current[active].scale || 1) * delta;
        local.current[active].scale = clamp(newScale, 0.6, 3);
        applyVisuals();
      }
    }
    function onUp(e) {
      pointers.delete(e.pointerId);
      if (pointers.size === 0) {
        // commit local to blocks
        commit(activeRef.current);
        modeG = null;
        lastDist = null;
      } else modeG = 'pan';
    }
    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  function clampWithinSafe(pxX, pxY, elW, elH, frameW) {
    const safe = computeSafe(frameW);
    const minX = safe.left;
    const maxX = safe.right - elW;
    const minY = safe.top;
    const maxY = frameW * 1.2 - elH; // rough bottom clamp based on aspect
    const nx = clamp(pxX, minX, Math.max(minX, maxX));
    const ny = clamp(pxY, minY, Math.max(minY, maxY));
    return { x: nx, y: ny };
  }

  function commit(key) {
    if (!interactionRef.current) return;
    const rect = interactionRef.current.getBoundingClientRect();
    const fw = rect.width,
      fh = rect.height;
    const b = local.current[key];
    const node = interactionRef.current.querySelector(
      `[data-block="${key}"] .edit`
    );
    const r = node ? node.getBoundingClientRect() : { width: 120, height: 40 };
    const px = { x: b.x * fw, y: b.y * fh };
    const clamped = clampWithinSafe(px.x, px.y, r.width, r.height, fw);
    const norm = { x: clamped.x / fw, y: clamped.y / fh };
    setBlocks((prev) => ({
      ...prev,
      [key]: { ...prev[key], x: norm.x, y: norm.y, scale: b.scale },
    }));
  }

  function onEditStart(which) {
    activeRef.current = which;
    // focus
    const node = interactionRef.current.querySelector(
      `[data-block="${which}"] .edit`
    );
    if (node) {
      node.focus();
    }
  }

  function updateSlideField(idx, key, val) {
    setSlides((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
  }

  function addSlide() {
    setSlides((prev) => [...prev, { title: '', body: '' }]);
  }
  function removeSlide() {
    if (slides.length <= 1) return;
    const next = slides.slice(0, -1);
    setSlides(next);
    setCurrentSlide(Math.max(0, next.length - 1));
  }

  async function exportAll() {
    // if single: export current; if carousel: export all slides
    if (mode === 'single') {
      await exportSlide(0);
    } else {
      for (let i = 0; i < slides.length; i++) {
        setCurrentSlide(i);
        // small delay to ensure render updated
        await new Promise((r) => setTimeout(r, 100));
        await exportSlide(i);
      }
    }
  }

  async function exportSlide(idx) {
    // build offscreen high-res node
    const off = document.createElement('div');
    off.style.width = TARGET_W + 'px';
    off.style.height = TARGET_H + 'px';
    off.style.position = 'fixed';
    off.style.left = '-9999px';
    off.style.top = '0';
    off.style.background = palette.bg;
    off.style.color = palette.text;
    off.style.fontFamily = 'Inter, system-ui, Arial, sans-serif';
    // safe margin
    const safe = SAFE_MARGIN_1080;
    // title
    const titleEl = document.createElement('div');
    titleEl.style.position = 'absolute';
    const b = blocks.title;
    titleEl.style.left = Math.round(b.x * TARGET_W) + 'px';
    titleEl.style.top = Math.round(b.y * TARGET_H) + 'px';
    titleEl.style.transform = `scale(${b.scale})`;
    titleEl.style.transformOrigin = 'top left';
    titleEl.style.fontFamily = 'Playfair Display, serif';
    titleEl.style.fontWeight = 700;
    titleEl.style.fontSize = '36px';
    titleEl.style.lineHeight = '1.02';
    titleEl.innerText =
      mode === 'single'
        ? slides[0].title || 'Title'
        : slides[idx].title || 'Title';

    const bodyEl = document.createElement('div');
    bodyEl.style.position = 'absolute';
    const bb = blocks.body;
    bodyEl.style.left = Math.round(bb.x * TARGET_W) + 'px';
    bodyEl.style.top = Math.round(bb.y * TARGET_H) + 'px';
    bodyEl.style.transform = `scale(${bb.scale})`;
    bodyEl.style.transformOrigin = 'top left';
    bodyEl.style.fontFamily = 'Poppins, sans-serif';
    bodyEl.style.fontSize = '18px';
    bodyEl.style.lineHeight = '1.08';
    bodyEl.innerText =
      mode === 'single' ? slides[0].body || 'Body' : slides[idx].body || 'Body';

    off.appendChild(titleEl);
    off.appendChild(bodyEl);
    document.body.appendChild(off);
    await document.fonts.ready;
    const canvas = await html2canvas(off, { backgroundColor: null, scale: 1 });
    const link = document.createElement('a');
    link.download = mode === 'single' ? 'post.png' : `slide_${idx + 1}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    off.remove();
  }

  // render
  return (
    <div
      className="app-root"
      style={{
        minHeight: '100vh',
        background: '#f4f4f6',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* top controls */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 8,
            alignItems: 'center',
          }}
        >
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            style={{ flex: 1, padding: 8, borderRadius: 8 }}
          >
            <option value="single">Single</option>
            <option value="carousel">Carousel</option>
          </select>
          <select
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            style={{ padding: 8, borderRadius: 8 }}
          >
            <option value="minimal">Minimal</option>
            <option value="poetic">Poetic</option>
            <option value="testimonial">Testimonial</option>
            <option value="quote">Quote</option>
          </select>
          <button
            onClick={() =>
              setPalette(
                palettes[(palettes.indexOf(palette) + 1) % palettes.length]
              )
            }
            style={{ padding: '8px 10px', borderRadius: 8 }}
          >
            Color
          </button>
        </div>

        {/* frame */}
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 6px 20px rgba(12,12,30,0.08)',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: `${TARGET_W}/${TARGET_H}`,
              background: palette.bg,
              color: palette.text,
              touchAction: 'none',
            }}
          >
            {/* render layer */}
            <div
              ref={renderRef}
              style={{ position: 'absolute', inset: 0, padding: 0 }}
            >
              {/* safe zone indicator */}
              <div
                style={{
                  position: 'absolute',
                  left: `${(SAFE_MARGIN_1080 / TARGET_W) * 100}%`,
                  top: `${(SAFE_MARGIN_1080 / TARGET_W) * 100}%`,
                  right: `${(SAFE_MARGIN_1080 / TARGET_W) * 100}%`,
                  bottom: 24,
                  pointerEvents: 'none',
                  border: '1px dashed rgba(255,255,255,0.06)',
                }}
              />
              {/* title/body */}
              <div
                style={{
                  position: 'absolute',
                  left: `${blocks.title.x * 100}%`,
                  top: `${blocks.title.y * 100}%`,
                  transform: `scale(${blocks.title.scale})`,
                  transformOrigin: 'top left',
                  paddingLeft: 24,
                  paddingRight: 24,
                }}
              >
                <div
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 700,
                    fontSize: 36,
                    lineHeight: 1,
                  }}
                >
                  {mode === 'single'
                    ? slides[0].title
                    : slides[currentSlide]?.title}
                </div>
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: `${blocks.body.x * 100}%`,
                  top: `${blocks.body.y * 100}%`,
                  transform: `scale(${blocks.body.scale})`,
                  transformOrigin: 'top left',
                  paddingLeft: 24,
                  paddingRight: 24,
                }}
              >
                <div
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.08,
                  }}
                >
                  {mode === 'single'
                    ? slides[0].body
                    : slides[currentSlide]?.body}
                </div>
              </div>
              {/* footer */}
              <div
                style={{
                  position: 'absolute',
                  left: SAFE_MARGIN_1080,
                  right: SAFE_MARGIN_1080,
                  bottom: 24,
                  fontFamily: 'Courier Prime, monospace',
                }}
              >
                <div style={{ fontSize: 16 }}>Series Title</div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>@handle</div>
              </div>
            </div>

            {/* interaction layer */}
            <div
              ref={interactionRef}
              style={{ position: 'absolute', inset: 0 }}
            >
              <div
                data-block="title"
                style={{ position: 'absolute', left: 0, top: 0 }}
              >
                <div
                  className="edit"
                  contentEditable
                  suppressContentEditableWarning
                  onFocus={() => onEditStart('title')}
                  onInput={(e) => {
                    const val = e.currentTarget.innerText;
                    if (mode === 'single') {
                      setSlides((prev) => {
                        const c = [...prev];
                        c[0].title = val;
                        return c;
                      });
                    } else {
                      setSlides((prev) => {
                        const c = [...prev];
                        c[currentSlide].title = val;
                        return c;
                      });
                    }
                  }}
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 700,
                    fontSize: 20,
                    lineHeight: 1,
                    paddingLeft: 8,
                    paddingRight: 8,
                    outline: 'none',
                    background: 'transparent',
                    color: palette.text,
                  }}
                >
                  {mode === 'single'
                    ? slides[0].title
                    : slides[currentSlide]?.title}
                </div>
              </div>

              <div
                data-block="body"
                style={{ position: 'absolute', left: 0, top: 0 }}
              >
                <div
                  className="edit"
                  contentEditable
                  suppressContentEditableWarning
                  onFocus={() => onEditStart('body')}
                  onInput={(e) => {
                    const val = e.currentTarget.innerText;
                    if (mode === 'single') {
                      setSlides((prev) => {
                        const c = [...prev];
                        c[0].body = val;
                        return c;
                      });
                    } else {
                      setSlides((prev) => {
                        const c = [...prev];
                        c[currentSlide].body = val;
                        return c;
                      });
                    }
                  }}
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 16,
                    lineHeight: 1.08,
                    paddingLeft: 8,
                    paddingRight: 8,
                    outline: 'none',
                    background: 'transparent',
                    color: palette.text,
                  }}
                >
                  {mode === 'single'
                    ? slides[0].body
                    : slides[currentSlide]?.body}
                </div>
              </div>
            </div>

            {/* interaction UI: slide controls */}
            {mode === 'carousel' && (
              <div
                style={{
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: 12,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => {
                      addSlide();
                    }}
                    style={{ padding: 8, borderRadius: 10 }}
                  >
                    + Add
                  </button>
                  <button
                    onClick={() => {
                      removeSlide();
                    }}
                    style={{ padding: 8, borderRadius: 10 }}
                  >
                    Remove
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    disabled={currentSlide <= 0}
                    onClick={() => setCurrentSlide((s) => Math.max(0, s - 1))}
                    style={{ padding: 8, borderRadius: 10 }}
                  >
                    Prev
                  </button>
                  <div>
                    Slide {currentSlide + 1} / {slides.length}
                  </div>
                  <button
                    disabled={currentSlide >= slides.length - 1}
                    onClick={() =>
                      setCurrentSlide((s) => Math.min(slides.length - 1, s + 1))
                    }
                    style={{ padding: 8, borderRadius: 10 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* below frame: export and controls */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => exportAll()}
            style={{ flex: 1, padding: 12, borderRadius: 10 }}
          >
            Export
          </button>
          <button
            onClick={() => {
              /* cycle template */ const keys = Object.keys(templates);
              setTemplateKey(
                keys[(keys.indexOf(templateKey) + 1) % keys.length]
              );
            }}
            style={{ padding: 12, borderRadius: 10 }}
          >
            Template
          </button>
        </div>

        {/* slides editor for carousel */}
        {mode === 'carousel' && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
            }}
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#666' }}>
                  Slide Title
                </label>
                <input
                  value={slides[currentSlide]?.title || ''}
                  onChange={(e) =>
                    updateSlideField(currentSlide, 'title', e.target.value)
                  }
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 8,
                    border: '1px solid #eee',
                  }}
                />
              </div>
              <div style={{ width: 100 }}>
                <label style={{ fontSize: 12, color: '#666' }}>Color</label>
                <select
                  onChange={(e) => setPalette(palettes[e.target.value])}
                  style={{ width: '100%', padding: 8, borderRadius: 8 }}
                >
                  {palettes.map((p, i) => (
                    <option value={i} key={i}>
                      {p.bg}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666' }}>Slide Body</label>
              <textarea
                value={slides[currentSlide]?.body || ''}
                onChange={(e) =>
                  updateSlideField(currentSlide, 'body', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 8,
                  border: '1px solid #eee',
                  minHeight: 80,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}